import React, { useState, useEffect, useCallback } from 'react';
import {
  Trophy,
  Star,
  Gift,
  Target,
  Users,
  Clock,
  Zap,
  X,
  Loader,
  Coins,
  CheckCircle,
  TrendingUp,
  CreditCard
} from 'lucide-react';

// Import centralized components
import MediCureOnSidebar from '../common/MediCureOnSidebar';
import MediCureOnHeader from '../common/MediCureOnHeader';

// Import API configuration
import { apiConfig } from '../../config/apiConfig';

// Import custom hook for subscription data (same as sidebar)
import { useMediCureOnData } from '../../hooks/useMediCureOnData';
import { useAuth } from '../../hooks/useAuth';

// Import tab components
import OverviewTab from './tabs/OverviewTab';
import EarnPointsTab from './tabs/EarnPointsTab';
import RedeemTab from './tabs/RedeemTab';
import ChallengesTab from './tabs/ChallengesTab';
import GlobalTop25Tab from './tabs/GlobalTop25Tab';
import EnterpriseGamificationHub from './tabs/EnterpriseGamificationHub';

const RewardsAchievementsDashboard = ({ onNavigate }) => {
  // Use the same data source as the sidebar
  const { 
    userSubscription, 
    refreshSubscription,
    mediPointsBalance, 
    fetchMediPoints,
    isBackendConnected 
  } = useMediCureOnData();
  
  // Get authentication context
  const { user, userInfo, getAccessToken } = useAuth();
  
  // Main state for the dashboard - with URL state management for refresh persistence
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize activeTab from URL hash to persist tab state across refreshes
    const hash = window.location.hash.substring(1); // Remove the '#' character
    const validTabs = ['overview', 'earn', 'rewards', 'challenges', 'leaderboard', 'elite-arena'];
    return validTabs.includes(hash) ? hash : 'overview';
  });
  
  // 🎯 PRODUCTION-READY: Challenge data sharing between tabs
  const [sharedChallengeData, setSharedChallengeData] = useState({
    userActiveChallenges: [],
    challengeProgress: {},
    userMediPoints: 0 // Will be updated by ChallengesTab
  });

  // 🚀 REAL DATA: Dynamic userData based on enterprise MediPoints system
  const userData = React.useMemo(() => {
    const activeChallenges = sharedChallengeData.userActiveChallenges || [];
    const completedChallenges = JSON.parse(localStorage.getItem(`completedChallenges_${user?.localAccountId}`) || '[]');
    
    // Use enterprise MediPoints balance from backend
    const enterpriseBalance = mediPointsBalance?.currentBalance || 0;
    
    return {
      points: enterpriseBalance, // Use enterprise balance instead of shared challenge data
      level: Math.floor(enterpriseBalance / 1000) + 1,
      tier: enterpriseBalance > 5000 ? 'Gold' : 
            enterpriseBalance > 2000 ? 'Silver' : 'Bronze',
      completedChallenges: completedChallenges.length,
      totalChallenges: activeChallenges.length + completedChallenges.length,
      weeklyProgress: 75, // Could be calculated from weekly challenge progress
      monthlyGoal: 10000,
      currentStreak: 0, // Could be calculated from daily challenge completion
      longestStreak: 0, // Could be calculated from challenge history
      globalRank: 0, // Will be calculated based on real user points comparison
      countryRank: 0, // Will be calculated based on real user points comparison
      badges: ['early_bird', 'consistent', 'achiever'], // Could be dynamic based on achievements
      // Enterprise MediPoints additional data
      totalEarned: mediPointsBalance?.totalEarned || 0,
      totalSpent: mediPointsBalance?.totalSpent || 0,
      totalRedeemed: mediPointsBalance?.totalRedeemed || 0,
      achievements: mediPointsBalance?.achievements || [],
      sourceBreakdown: mediPointsBalance?.sourceBreakdown || {},
      isBackendConnected: isBackendConnected
    };
  }, [sharedChallengeData, user?.localAccountId, mediPointsBalance, isBackendConnected]);

  const [loading, setLoading] = useState(true);
  const [refreshingSubscription, setRefreshingSubscription] = useState(false);
  const [notification, setNotification] = useState(null);

  // Function to update shared challenge data from ChallengesTab
  const updateSharedChallengeData = useCallback((newData) => {
    setSharedChallengeData(prev => ({
      ...prev,
      ...newData
    }));
  }, []);

  // Function to handle tab switching with URL state management
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    // Update URL hash to persist tab state across refreshes
    window.history.replaceState(null, '', `#${tabId}`);
  }, []); // No dependencies needed since setSharedChallengeData is stable

  // Create subscription info object from hook data to maintain compatibility
  const subscriptionInfo = {
    tier: userSubscription?.type ? 
      userSubscription.type.charAt(0).toUpperCase() + userSubscription.type.slice(1).toLowerCase() : 
      'Standard',
    expiryDate: userSubscription?.currentPeriodEnd || '2024-12-31',
    benefits: ['discount_consultations', 'priority_booking', 'wellness_rewards', 'unlimited_challenges'],
    isActive: userSubscription?.status === 'active' || true,
    lastUpdated: new Date().toISOString(),
    source: 'useMediCureOnData-hook'
  };

  // Debug logging to track subscription changes
  React.useEffect(() => {
    console.log('🔍 Dashboard subscription update:', {
      hookData: userSubscription,
      processedData: subscriptionInfo,
      timestamp: new Date().toISOString()
    });
  }, [userSubscription, subscriptionInfo]);

  // 🚀 FETCH ENTERPRISE MEDIPOINTS ON COMPONENT LOAD
  useEffect(() => {
    const loadMediPoints = async () => {
      try {
        console.log('🎯 Loading enterprise MediPoints balance...');
        await fetchMediPoints();
        console.log('✅ MediPoints loaded:', mediPointsBalance);
      } catch (error) {
        console.error('❌ Error loading MediPoints:', error);
      }
    };

    if (user && isBackendConnected && !mediPointsBalance) {
      loadMediPoints();
    }
  }, [user, isBackendConnected, fetchMediPoints, mediPointsBalance]);

  // 🚀 USE SAME SUBSCRIPTION SOURCE AS SIDEBAR
  const checkSubscriptionStatus = async () => {
    setRefreshingSubscription(true);
    try {
      console.log('🔍 Refreshing subscription status using sidebar hook...');
      
      // Use the same refresh function as the sidebar
      await refreshSubscription();
      
      console.log('✅ Subscription refreshed from hook:', userSubscription);
      
      setNotification({
        message: `✅ Subscription updated: ${userSubscription?.type || 'Standard'} Plan Active`,
        type: 'success',
        show: true
      });
      
    } catch (error) {
      console.error('❌ Subscription refresh error:', error.message);
      
      setNotification({
        message: `❌ Error refreshing subscription: ${error.message}`,
        type: 'error',
        show: true
      });
    } finally {
      setRefreshingSubscription(false);
    }
  };

  // Load challenge data directly from backend database
  useEffect(() => {
    const loadChallengeDataFromBackend = async () => {
      if (!user?.localAccountId && !user?.username && !userInfo?.localAccountId) {
        console.warn('⚠️ No user ID available for loading challenge data from backend');
        return;
      }

      try {
        const userId = user?.localAccountId || user?.username || userInfo?.localAccountId;
        // 🔧 STANDARDIZED: Use same country logic as useMediCureOnData hook for consistency
        const userCountry = userInfo?.country || 
                           userInfo?.personalDetails?.country || 
                           user?.idTokenClaims?.country || 
                           user?.country ||
                           'United States';
        
        console.log(`🔄 Dashboard: Loading challenge data from backend for user ${userId}`);
        console.log(`🌍 Dashboard: Country determined as: ${userCountry}`);
        
        // Use same backend call as ChallengesTab
        const response = await fetch(`${apiConfig.backendUrl}/api/challenges-manager?code=${apiConfig.functionKeys.challengesManager}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user?.idToken}`,
            'X-User-Country': userCountry
          },
          body: JSON.stringify({
            action: 'get',
            userId: userId,
            country: userCountry
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('🏆 Dashboard: Backend challenge data loaded:', result);
          
          const challengeData = result.data?.challengeData || {};
          console.log('📊 Dashboard: Active challenges from backend:', challengeData.activeChallenges);
          
          // Update shared challenge data with real backend data
          setSharedChallengeData(prev => ({
            ...prev,
            userActiveChallenges: challengeData.activeChallenges || [],
            challengeProgress: challengeData.challengeProgress || {}
          }));
        } else {
          console.warn('⚠️ Dashboard: Backend challenge data load failed, falling back to localStorage');
          initializeChallengeDataFromLocalStorage();
        }
      } catch (error) {
        console.error('❌ Dashboard: Error loading challenge data from backend:', error);
        initializeChallengeDataFromLocalStorage();
      }
    };

    const initializeChallengeDataFromLocalStorage = () => {
      if (!user?.localAccountId && !user?.username && !userInfo?.localAccountId) {
        console.warn('⚠️ No user ID available for loading challenge data in dashboard');
        return;
      }

      try {
        const userId = user?.localAccountId || user?.username || userInfo?.localAccountId;
        
        // Use the same localStorage keys as ChallengesTab
        const challengeKey = `challenges_${userId}`;
        const fallbackKey = `challenges_fallback_${userId}`;
        
        // Try main storage first, then fallback
        let storedData = localStorage.getItem(challengeKey);
        let source = 'main';
        
        if (!storedData) {
          storedData = localStorage.getItem(fallbackKey);
          source = 'fallback';
        }
        
        if (storedData) {
          const parsed = JSON.parse(storedData);
          console.log(`🏆 Dashboard: Loaded challenge data from ${source} localStorage:`, parsed);
          
          // Update shared challenge data with real data from localStorage
          // Handle both data structures: activeChallenges (from fallback) and userActiveChallenges (from main)
          if (source === 'fallback') {
            setSharedChallengeData(prev => ({
              ...prev,
              userActiveChallenges: parsed.activeChallenges || [],
              challengeProgress: parsed.challengeProgress || {}
            }));
          } else {
            setSharedChallengeData(prev => ({
              ...prev,
              userActiveChallenges: parsed.userActiveChallenges || parsed.activeChallenges || [],
              challengeProgress: parsed.challengeProgress || {}
            }));
          }
        } else {
          console.log('📭 Dashboard: No challenge data found in localStorage');
        }
      } catch (error) {
        console.error('❌ Dashboard: Error loading challenge data:', error);
      }
    };

    // Try backend first, fallback to localStorage
    loadChallengeDataFromBackend();
  }, [user?.localAccountId, user?.username, userInfo?.localAccountId, user?.country, user?.idToken, userInfo?.country, userInfo?.personalDetails?.country, user?.idTokenClaims?.country]);

  // Listen for challenge data updates from ChallengesTab for real-time sync
  useEffect(() => {
    const handleChallengeDataUpdate = () => {
      console.log('🔄 Dashboard: Received challengeDataUpdated event, reloading data...');
      // Reload challenge data when the event is triggered
      if (!user?.localAccountId && !user?.username && !userInfo?.localAccountId) return;

      try {
        const userId = user?.localAccountId || user?.username || userInfo?.localAccountId;
        const challengeKey = `challenges_${userId}`;
        const fallbackKey = `challenges_fallback_${userId}`;
        
        let storedData = localStorage.getItem(challengeKey);
        let source = 'main';
        
        if (!storedData) {
          storedData = localStorage.getItem(fallbackKey);
          source = 'fallback';
        }
        
        if (storedData) {
          const parsed = JSON.parse(storedData);
          console.log(`🏆 Dashboard: Reloaded challenge data from ${source} localStorage:`, parsed);
          
          // Handle both data structures: activeChallenges (from fallback) and userActiveChallenges (from main)
          if (source === 'fallback') {
            setSharedChallengeData(prev => ({
              ...prev,
              userActiveChallenges: parsed.activeChallenges || [],
              challengeProgress: parsed.challengeProgress || {}
            }));
          } else {
            setSharedChallengeData(prev => ({
              ...prev,
              userActiveChallenges: parsed.userActiveChallenges || parsed.activeChallenges || [],
              challengeProgress: parsed.challengeProgress || {}
            }));
          }
        }
      } catch (error) {
        console.error('❌ Dashboard: Error reloading challenge data:', error);
      }
    };

    window.addEventListener('challengeDataUpdated', handleChallengeDataUpdate);
    
    return () => {
      window.removeEventListener('challengeDataUpdated', handleChallengeDataUpdate);
    };
  }, [user?.localAccountId, user?.username, userInfo?.localAccountId]);

  // Sample challenge data for the original design
  const [userChallenges, setUserChallenges] = useState({
    active: [
      {
        id: 'walk-10k',
        title: '10,000 Steps Daily',
        description: 'Walk 10,000 steps for 7 consecutive days',
        currentProgress: 7500,
        targetValue: 10000,
        unit: 'steps',
        points: 500,
        difficulty: 'easy',
        duration: '7 days'
      },
      {
        id: 'hydration',
        title: 'Hydration Hero',
        description: 'Drink 8 glasses of water daily for 5 days',
        currentProgress: 6,
        targetValue: 8,
        unit: 'glasses',
        points: 300,
        difficulty: 'beginner',
        duration: '5 days'
      }
    ],
    completed: [
      {
        id: 'morning-exercise',
        title: 'Morning Warrior',
        description: 'Complete 30-minute morning workout for 10 days',
        points: 750,
        difficulty: 'medium',
        completedDate: '2024-01-15'
      }
      // 🚨 FIXED: Removed hardcoded sleep-schedule completion entry
      // This was causing Sleep Schedule to show "Completed" + "Retry for Bonus" 
      // when user never actually started this challenge
    ],
    failed: [
      {
        id: 'meditation',
        title: 'Mindful Minutes',
        description: 'Meditate for 15 minutes daily for 21 days',
        points: 800,
        difficulty: 'hard',
        failedDate: '2024-01-05'
      }
    ]
  });

  const [challenges] = useState([
    // BEGINNER CHALLENGES (100 points) - Perfect for seniors and newcomers
    {
      id: 1,
      name: 'First Day Champion',
      description: 'Complete all daily activities in a single day',
      icon: '🌱',
      points: 100,
      duration: '1 day',
      progress: 0,
      total: 1,
      participants: 15234,
      difficulty: 'beginner',
      gradient: 'from-green-300 to-green-500',
      active: true,
      requirement: 'Log meals, water, weight, and sleep',
      category: 'daily',
      seniorFriendly: true
    },
    {
      id: 2,
      name: 'Water Warrior',
      description: 'Drink 8 glasses of water today',
      icon: '💧',
      points: 100,
      duration: '1 day',
      progress: 5,
      total: 8,
      participants: 12543,
      difficulty: 'beginner',
      gradient: 'from-blue-300 to-blue-500',
      active: true,
      requirement: 'Track each glass',
      category: 'nutrition',
      seniorFriendly: true
    },
    {
      id: 3,
      name: 'Rest & Recharge',
      description: 'Get 7+ hours of sleep tonight',
      icon: '😴',
      points: 100,
      duration: '1 night',
      progress: 0,
      total: 1,
      participants: 9876,
      difficulty: 'beginner',
      gradient: 'from-purple-300 to-purple-500',
      active: true,
      requirement: 'Track sleep duration',
      category: 'wellness',
      seniorFriendly: true
    },
    {
      id: 4,
      name: 'Medication Master',
      description: 'Take all medications on time for 3 days',
      icon: '💊',
      points: 100,
      duration: '3 days',
      progress: 1,
      total: 3,
      participants: 8234,
      difficulty: 'beginner',
      gradient: 'from-pink-300 to-pink-500',
      active: true,
      requirement: 'Log medication times',
      category: 'health',
      seniorFriendly: true
    },
    {
      id: 5,
      name: 'Gentle Mover',
      description: 'Walk for 10 minutes today',
      icon: '🚶‍♀️',
      points: 100,
      duration: '1 day',
      progress: 0,
      total: 10,
      participants: 11234,
      difficulty: 'beginner',
      gradient: 'from-yellow-300 to-yellow-500',
      active: true,
      requirement: 'Any pace counts',
      category: 'activity',
      seniorFriendly: true
    },

    // EASY CHALLENGES (500 points)
    {
      id: 6,
      name: '3-Day Streak Starter',
      description: 'Log health data for 3 consecutive days',
      icon: '📅',
      points: 500,
      duration: '3 days',
      progress: 1,
      total: 3,
      participants: 8234,
      difficulty: 'easy',
      gradient: 'from-green-400 to-green-600',
      active: true,
      requirement: 'Complete daily check-ins',
      category: 'consistency'
    },
    {
      id: 7,
      name: '5K Step Day',
      description: 'Walk 5,000 steps in a single day',
      icon: '👟',
      points: 500,
      duration: '1 day',
      progress: 2300,
      total: 5000,
      participants: 9456,
      difficulty: 'easy',
      gradient: 'from-blue-400 to-blue-600',
      active: true,
      requirement: 'Track with connected device',
      category: 'activity'
    },
    {
      id: 8,
      name: 'Hydration Week',
      description: 'Meet water goals for 7 days',
      icon: '💦',
      points: 500,
      duration: '7 days',
      progress: 3,
      total: 7,
      participants: 7654,
      difficulty: 'easy',
      gradient: 'from-cyan-400 to-cyan-600',
      active: true,
      requirement: '8 glasses daily',
      category: 'nutrition'
    },
    {
      id: 9,
      name: 'Sleep Schedule',
      description: 'Sleep 7+ hours for 5 nights',
      icon: '🛌',
      points: 500,
      duration: '5 days',
      progress: 2,
      total: 5,
      participants: 6789,
      difficulty: 'easy',
      gradient: 'from-indigo-400 to-indigo-600',
      active: true,
      requirement: 'Track sleep patterns',
      category: 'wellness'
    },
    {
      id: 10,
      name: 'Meal Logger',
      description: 'Log all meals for 5 days',
      icon: '🍽️',
      points: 500,
      duration: '5 days',
      progress: 2,
      total: 5,
      participants: 5432,
      difficulty: 'easy',
      gradient: 'from-orange-400 to-orange-600',
      active: true,
      requirement: 'Breakfast, lunch, dinner',
      category: 'nutrition'
    },

    // MEDIUM CHALLENGES (2,500 points)
    {
      id: 11,
      name: '30-Day Transformation',
      description: 'Complete a full health plan in 30 days',
      icon: '💪',
      points: 2500,
      duration: '30 days',
      progress: 12,
      total: 30,
      participants: 3421,
      difficulty: 'medium',
      gradient: 'from-purple-400 to-purple-600',
      active: true,
      requirement: 'Must be enrolled in a plan',
      category: 'plans'
    },
    {
      id: 12,
      name: '100K Step Sprint',
      description: 'Walk 100,000 steps in 2 weeks',
      icon: '🏃‍♀️',
      points: 2500,
      duration: '14 days',
      progress: 45000,
      total: 100000,
      participants: 2876,
      difficulty: 'medium',
      gradient: 'from-pink-400 to-pink-600',
      active: true,
      requirement: '~7,143 steps daily',
      category: 'activity'
    },
    {
      id: 13,
      name: 'Weight Loss Kickstart',
      description: 'Lose 5kg (11lbs) in 45 days',
      icon: '⚖️',
      points: 2500,
      duration: '45 days',
      progress: 2.1,
      total: 5,
      participants: 2345,
      difficulty: 'medium',
      gradient: 'from-green-400 to-teal-600',
      active: true,
      requirement: 'Healthy weight loss pace',
      category: 'weight'
    },
    {
      id: 14,
      name: 'Mindfulness Month',
      description: 'Meditate 20 minutes daily for 30 days',
      icon: '🧘',
      points: 2500,
      duration: '30 days',
      progress: 8,
      total: 30,
      participants: 1987,
      difficulty: 'medium',
      gradient: 'from-teal-400 to-blue-600',
      active: true,
      requirement: 'Any meditation app',
      category: 'wellness'
    },
    {
      id: 15,
      name: 'Community Helper',
      description: 'Help 25 community members',
      icon: '🤝',
      points: 2500,
      duration: '60 days',
      progress: 12,
      total: 25,
      participants: 1654,
      difficulty: 'medium',
      gradient: 'from-yellow-400 to-red-600',
      active: true,
      requirement: 'Verified helpful responses',
      category: 'social'
    },

    // HARD CHALLENGES (5,000 points)
    {
      id: 16,
      name: '500K Step Journey',
      description: 'Walk 500,000 steps in 60 days',
      icon: '🚶',
      points: 5000,
      duration: '60 days',
      progress: 125000,
      total: 500000,
      participants: 2341,
      difficulty: 'hard',
      gradient: 'from-blue-500 to-purple-600',
      active: true,
      requirement: '~8,333 steps daily',
      category: 'activity'
    },
    {
      id: 17,
      name: '90-Day Streak Master',
      description: 'Maintain perfect daily logging for 90 days',
      icon: '🔥',
      points: 5000,
      duration: '90 days',
      progress: 15,
      total: 90,
      participants: 1892,
      difficulty: 'hard',
      gradient: 'from-orange-500 to-red-600',
      active: true,
      requirement: 'No missed days',
      category: 'consistency'
    },
    {
      id: 18,
      name: 'Fitness Revolution',
      description: 'Complete 100 workouts in 120 days',
      icon: '💯',
      points: 5000,
      duration: '120 days',
      progress: 23,
      total: 100,
      participants: 1456,
      difficulty: 'hard',
      gradient: 'from-purple-500 to-pink-600',
      active: true,
      requirement: '30+ minute sessions',
      category: 'activity'
    },
    {
      id: 19,
      name: 'Nutrition Expert',
      description: 'Track macros perfectly for 60 days',
      icon: '📊',
      points: 5000,
      duration: '60 days',
      progress: 12,
      total: 60,
      participants: 987,
      difficulty: 'hard',
      gradient: 'from-green-500 to-emerald-600',
      active: true,
      requirement: 'Within 5% of targets',
      category: 'nutrition'
    },
    {
      id: 20,
      name: 'Multi-Plan Master',
      description: 'Complete 3 different health plans',
      icon: '🎯',
      points: 5000,
      duration: '180 days',
      progress: 1,
      total: 3,
      participants: 765,
      difficulty: 'hard',
      gradient: 'from-indigo-500 to-purple-600',
      active: true,
      requirement: 'Different plan types',
      category: 'plans'
    },

    // EXTREME CHALLENGES (10,000 points each)
    {
      id: 21,
      name: 'Million Step Marathon',
      description: 'Walk 1,000,000 steps in 90 days - that\'s over 11,000 steps daily!',
      icon: '🏃‍♂️',
      points: 10000,
      duration: '90 days',
      progress: 342580,
      total: 1000000,
      participants: 1234,
      difficulty: 'extreme',
      gradient: 'from-purple-500 to-pink-500',
      active: true,
      requirement: 'Track steps daily via connected device',
      category: 'activity'
    },
    {
      id: 22,
      name: 'Triple Threat Champion',
      description: 'Start 3 health plans and achieve all goals within 90 days',
      icon: '🏆',
      points: 10000,
      duration: '90 days',
      progress: 1,
      total: 3,
      participants: 892,
      difficulty: 'extreme',
      gradient: 'from-yellow-400 to-orange-500',
      active: true,
      requirement: 'Complete 3 different health plans',
      category: 'plans'
    },
    {
      id: 23,
      name: 'Weight Loss Warrior',
      description: 'Lose 20kg (44lbs) in 6 months with healthy habits',
      icon: '⚖️',
      points: 10000,
      duration: '180 days',
      progress: 7.5,
      total: 20,
      participants: 567,
      difficulty: 'extreme',
      gradient: 'from-green-500 to-teal-500',
      active: true,
      requirement: 'Medical supervision recommended',
      category: 'weight'
    },
    {
      id: 24,
      name: '365 Day Streak Legend',
      description: 'Log your health data every single day for an entire year',
      icon: '🔥',
      points: 10000,
      duration: '365 days',
      progress: 45,
      total: 365,
      participants: 2103,
      difficulty: 'extreme',
      gradient: 'from-red-500 to-orange-600',
      active: true,
      requirement: 'No missed days allowed',
      category: 'consistency'
    },
    {
      id: 25,
      name: 'Meditation Master',
      description: 'Complete 500 meditation sessions of 20+ minutes each',
      icon: '🧘',
      points: 10000,
      duration: '180 days',
      progress: 127,
      total: 500,
      participants: 445,
      difficulty: 'extreme',
      gradient: 'from-indigo-500 to-purple-600',
      active: true,
      requirement: 'Verified through app tracking',
      category: 'wellness'
    },
    {
      id: 26,
      name: 'Ultimate Transformation',
      description: 'Complete all 10 health plans with 100% success rate',
      icon: '🌟',
      points: 10000,
      duration: '365 days',
      progress: 2,
      total: 10,
      participants: 89,
      difficulty: 'extreme',
      gradient: 'from-yellow-500 via-purple-500 to-pink-500',
      active: true,
      requirement: 'The ultimate MediCureOn achievement',
      category: 'plans'
    }
  ]);

  // Helper to get user challenge status by challenge id - EXACT from backup
  const getUserChallengeStatus = (challengeId) => {
    // Check if challenge is active
    const activeChallenge = userChallenges.active.find(c => c.id === challengeId);
    if (activeChallenge) return { status: 'active', data: activeChallenge };
    
    // Check if challenge is completed
    const completedChallenge = userChallenges.completed.find(c => c.id === challengeId);
    if (completedChallenge) {
      const completedDate = new Date(completedChallenge.completedDate);
      const daysSinceCompletion = Math.floor((new Date() - completedDate) / (1000 * 60 * 60 * 24));
      return { 
        status: 'completed', 
        data: completedChallenge,
        canRetry: daysSinceCompletion >= 365,
        daysUntilRetry: Math.max(0, 365 - daysSinceCompletion)
      };
    }
    
    // Check if challenge failed
    const failedChallenge = userChallenges.failed.find(c => c.id === challengeId);
    if (failedChallenge) return { status: 'failed', data: failedChallenge };
    
    return { status: 'available' };
  };

  // Enhanced Challenge progress update function with API integration
  const updateChallengeProgress = async (challengeId, newProgress, metadata = {}) => {
    try {
      console.log(`🔄 Updating challenge ${challengeId} progress to ${newProgress}`);
      
      // TODO: Replace with proper API call once backend is implemented
      showNotification('✅ Challenge progress updated successfully!');
      
    } catch (error) {
      console.error('❌ Error updating challenge progress:', error);
      showNotification('❌ Error updating challenge progress');
    }
  };

  // Production challenges data (matching ChallengesTab)
  const productionChallenges = {
    beginner: [
      {
        id: 'gentle-morning-walker',
        name: 'Gentle Morning Walker',
        description: 'Walk 500 steps before 9AM daily for 7 days',
        icon: '🌅',
        difficulty: 'beginner',
        duration: '7 days',
        points: 150
      },
      {
        id: 'hydration-hero',
        name: 'Hydration Hero',
        description: 'Drink 7+ glasses of water before 6PM daily for 7 days',
        icon: '💧',
        difficulty: 'beginner',
        duration: '7 days',
        points: 120
      },
      {
        id: 'sleep-starter',
        name: 'Sleep Starter',
        description: 'Sleep 7+ hours for 7 nights consecutively',
        icon: '😴',
        difficulty: 'beginner',
        duration: '7 days',
        points: 200
      }
    ]
  };

  // Load user challenges with new API
  const loadUserChallenges = useCallback(async () => {
    try {
      console.log('🔍 Loading user challenges...');
      
      // TODO: Replace with proper API call once backend is implemented
      console.log('✅ User challenges loaded (using local data)');
    } catch (error) {
      console.error('❌ Error loading user challenges:', error);
    }
  }, [user, userInfo, getAccessToken]);

  // Load user challenges on component mount - placed after loadUserChallenges definition
  useEffect(() => {
    if (user || userInfo) {
      console.log('🚀 Loading challenges for authenticated user...');
      loadUserChallenges();
    }
  }, [user, userInfo, loadUserChallenges]);

  // Join challenge function with new API integration
  const joinChallenge = async (challengeId) => {
    try {
      console.log('🚀 Joining challenge:', challengeId);
      
      // TODO: Replace with proper API call once backend is implemented
      showNotification(`🎉 Successfully joined challenge!`);
    } catch (error) {
      console.error('❌ Error joining challenge:', error);
      showNotification(`❌ Error joining challenge: ${error.message}`);
    }
  };

  // Get challenge status with new API integration
  const getChallengeStatus = async () => {
    try {
      console.log('🔍 Getting challenge status...');
      await loadUserChallenges();
    } catch (error) {
      console.error('❌ Error getting challenge status:', error);
    }
  };

  // Leave challenge function with new API integration
  const leaveChallenge = async (challengeId) => {
    try {
      console.log('🛑 Leaving challenge:', challengeId);
      
      // TODO: Replace with proper API call once backend is implemented
      showNotification('✅ Successfully left challenge');
    } catch (error) {
      console.error('❌ Error leaving challenge:', error);
      showNotification(`❌ Error leaving challenge: ${error.message}`);
    }
  };

  // Shared state that tabs might need
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [showChallengeDetails, setShowChallengeDetails] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showMediCoinModal, setShowMediCoinModal] = useState(false);

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      try {
        console.log('🚀 Dashboard initializing with sidebar subscription data:', userSubscription);
        
        // Simulate API calls
        await new Promise(resolve => setTimeout(resolve, 1000));
        setLoading(false);
      } catch (error) {
        console.error('Error initializing dashboard data:', error);
        setLoading(false);
      }
    };

    initializeData();
  }, [userSubscription]);

  // Show notification
  const showNotification = (message) => {
    setNotification({ message });
    setTimeout(() => setNotification(null), 3000);
  };

  // Shared functions that tabs might need
  const handleRewardRedeem = (reward) => {
    setSelectedReward(reward);
    setShowRedeemModal(true);
  };

  const handleChallengeClick = (challenge) => {
    setSelectedChallenge(challenge);
    setShowChallengeDetails(true);
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setShowDiscountModal(true);
  };

  const updateUserData = async (newData) => {
    // Handle enterprise MediPoints updates (e.g., redemptions)
    if (newData.points !== undefined && mediPointsBalance?.currentBalance !== undefined) {
      const pointsDifference = newData.points - mediPointsBalance.currentBalance;
      
      if (pointsDifference < 0) {
        // This is a redemption (negative points)
        try {
          console.log('💰 Processing redemption:', Math.abs(pointsDifference), 'points');
          
          // Use the medipointsApiService to handle the redemption
          const mediPointsService = (await import('../../services/medipointsApiService')).default;
          await mediPointsService.redeemPoints(Math.abs(pointsDifference), 'Healthcare Service Redemption');
          
          // Refresh the balance after redemption
          await fetchMediPoints();
          
          console.log('✅ Redemption processed successfully');
        } catch (error) {
          console.error('❌ Error processing redemption:', error);
          // Show error notification
          setNotification({
            message: 'Failed to process redemption. Please try again.',
            type: 'error',
            show: true
          });
        }
      }
    }
    
    // For legacy compatibility, also update shared challenge data
    if (newData.points !== undefined) {
      setSharedChallengeData(prev => ({
        ...prev,
        userMediPoints: newData.points
      }));
    }
    
    console.log('💾 User data update request:', newData);
  };

  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-50">
        <MediCureOnSidebar onNavigate={onNavigate} activePage="rewards" />
        <div className="flex-1 overflow-auto">
          <MediCureOnHeader
            title="Medi Rewards & Achievements"
            subtitle="Track your wellness journey and earn rewards"
            onNavigate={onNavigate}
          />
          <div className="flex items-center justify-center h-64">
            <Loader className="w-8 h-8 text-purple-600 animate-spin" />
            <span className="ml-2 text-gray-600">Loading your rewards...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Use centralized sidebar */}
      <MediCureOnSidebar 
        onNavigate={onNavigate} 
        activePage="rewards"
      />

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Use centralized header */}
        <MediCureOnHeader
          title="Medi Rewards & Achievements"
          subtitle="Track your wellness journey and earn rewards"
          onNavigate={onNavigate}
        />

        {/* 🚀 REAL-TIME SUBSCRIPTION STATUS BANNER */}
        <div className="px-6 py-3 border-b border-purple-100 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">
                {subscriptionInfo.tier === 'Loading...' ? '⏳' :
                 subscriptionInfo.tier === 'Platinum' ? '💎' : 
                 subscriptionInfo.tier === 'Gold' ? '🥇' : 
                 subscriptionInfo.tier === 'Silver' ? '🥈' : '🥉'}
              </span>
              <div>
                <span className="text-sm font-semibold text-purple-800">
                  Current Plan: {subscriptionInfo.tier}
                </span>
                {subscriptionInfo.lastUpdated && (
                  <p className="text-xs text-purple-600">
                    Last synced: {new Date(subscriptionInfo.lastUpdated).toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={checkSubscriptionStatus}
              disabled={refreshingSubscription}
              className={`px-3 py-1 text-xs font-medium transition-colors border rounded-lg ${
                refreshingSubscription 
                  ? 'text-gray-500 border-gray-300 bg-gray-100 cursor-not-allowed' 
                  : 'text-purple-700 border-purple-300 hover:bg-purple-100'
              }`}
            >
              {refreshingSubscription ? (
                <>
                  <Loader className="inline w-3 h-3 mr-1 animate-spin" />
                  Checking...
                </>
              ) : (
                '🔄 Refresh Status'
              )}
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="p-6">
          <div className="mb-6 border-b border-gray-200">
            <nav className="flex space-x-8">
              {[
                { id: 'overview', label: 'Overview', icon: Trophy },
                { id: 'earn', label: 'Earn Points', icon: Star },
                { id: 'rewards', label: 'Redeem', icon: Gift },
                { id: 'challenges', label: 'Challenges', icon: Target },
                { id: 'leaderboard', label: 'Global Top 25', icon: Users },
                { id: 'elite-arena', label: 'Elite Arena ⚡', icon: Zap }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center space-x-2 pb-4 border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="min-h-screen">
            {activeTab === 'overview' && (
              <OverviewTab
                userData={userData}
                subscriptionInfo={subscriptionInfo}
                onNavigate={onNavigate}
                onTabNavigate={setActiveTab}
                onServiceClick={handleServiceClick}
                showNotification={showNotification}
                updateUserData={updateUserData}
                userActiveChallenges={sharedChallengeData.userActiveChallenges}
                challengeProgress={sharedChallengeData.challengeProgress}
                userMediPoints={sharedChallengeData.userMediPoints}
                productionChallenges={productionChallenges}
                user={user}
                userInfo={userInfo}
              />
            )}

            {activeTab === 'earn' && (
              <EarnPointsTab
                userData={userData}
                onServiceClick={handleServiceClick}
                showNotification={showNotification}
                updateUserData={updateUserData}
              />
            )}

            {activeTab === 'rewards' && (
              <RedeemTab
                userData={userData}
                subscriptionInfo={subscriptionInfo}
                setSelectedReward={setSelectedReward}
                setShowRedeemModal={setShowRedeemModal}
                setShowMediCoinModal={setShowMediCoinModal}
                handleCardClick={handleServiceClick}
                onNavigate={onNavigate}
              />
            )}

            {activeTab === 'challenges' && (
              <div>
                <ChallengesTab
                  user={user}
                  userInfo={userInfo}
                  userChallenges={userChallenges}
                  challenges={challenges}
                  updateChallengeProgress={updateChallengeProgress}
                  joinChallenge={joinChallenge}
                  leaveChallenge={leaveChallenge}
                  getChallengeStatus={getChallengeStatus}
                  getUserChallengeStatus={getUserChallengeStatus}
                  setSelectedChallenge={setSelectedChallenge}
                  setShowChallengeDetails={setShowChallengeDetails}
                  userSubscription={subscriptionInfo}
                  updateSharedChallengeData={updateSharedChallengeData}
                />
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <GlobalTop25Tab
                userData={userData}
                showNotification={showNotification}
              />
            )}

            {/* Elite Arena - The Ultimate Wellness Competition Platform */}
            {activeTab === 'elite-arena' && (
              <EnterpriseGamificationHub
                userData={userData}
                showNotification={showNotification}
              />
            )}
          </div>
        </div>
      </div>

      {/* Shared Modals */}
      {/* Reward Redeem Modal */}
      {showRedeemModal && selectedReward && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 bg-white rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Confirm Redemption</h3>
              <button
                onClick={() => setShowRedeemModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Redeem <strong>{selectedReward.name}</strong> for <strong>{selectedReward.cost} Medi Points</strong>?
              </p>
              <p className="mt-2 text-sm text-gray-500">
                You currently have {userData.points} points.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowRedeemModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 transition-all bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateUserData({ points: userData.points - selectedReward.cost });
                  showNotification(`Successfully redeemed ${selectedReward.name}!`);
                  setShowRedeemModal(false);
                }}
                className="flex-1 px-4 py-2 text-white transition-all bg-purple-600 rounded-lg hover:bg-purple-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Challenge Details Modal */}
      {showChallengeDetails && selectedChallenge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-lg p-6 bg-white rounded-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{selectedChallenge.name}</h3>
                <p className="mt-1 text-sm text-gray-600">{selectedChallenge.description}</p>
              </div>
              <button
                onClick={() => setShowChallengeDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-purple-50">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-purple-900">Reward</span>
                  <span className="font-bold text-purple-600">{selectedChallenge.reward} points</span>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">Progress</span>
                  <span className="text-gray-600">{selectedChallenge.progress}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div 
                    className="h-2 bg-purple-600 rounded-full" 
                    style={{ width: `${selectedChallenge.progress}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowChallengeDetails(false)}
              className="w-full px-4 py-2 mt-6 text-white transition-all bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Discount Information Modal */}
      {showDiscountModal && selectedService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-lg p-6 bg-white rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">💎 Member Discount Information</h3>
              <button
                onClick={() => setShowDiscountModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <h4 className="mb-2 text-lg font-semibold text-gray-900">{selectedService}</h4>
              <p className="mb-4 text-gray-600">
                As a <strong>{subscriptionInfo?.tier}</strong> member, you're eligible for exclusive discounts on this service.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-4 text-center rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold text-purple-600">15%</p>
                  <p className="text-sm text-gray-600">Member Discount</p>
                </div>
                <div className="p-4 text-center rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold text-green-600">$50</p>
                  <p className="text-sm text-gray-600">Average Savings</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div className="p-4 text-center rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold text-purple-600">50K+</p>
                  <p className="text-sm text-gray-600">Active Holders</p>
                </div>
                <div className="p-4 text-center rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold text-green-600">$2.5M</p>
                  <p className="text-sm text-gray-600">Total Savings</p>
                </div>
                <div className="p-4 text-center rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold text-blue-600">98%</p>
                  <p className="text-sm text-gray-600">User Satisfaction</p>
                </div>
                <div className="p-4 text-center rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold text-yellow-600">24/7</p>
                  <p className="text-sm text-gray-600">Premium Support</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowDiscountModal(false)}
              className="w-full px-6 py-2 text-white transition-all bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* MediCoin Benefits Modal */}
      {showMediCoinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
            {/* Modal Header */}
            <div className="relative p-6 text-white bg-gradient-to-r from-purple-600 via-blue-600 to-green-500">
              <button
                onClick={() => setShowMediCoinModal(false)}
                className="absolute p-2 transition-all rounded-full top-4 right-4 bg-white/20 backdrop-blur-sm hover:bg-white/30"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="text-center">
                <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 rounded-full bg-white/20 backdrop-blur-sm">
                  <Coins className="w-12 h-12 text-yellow-300" />
                </div>
                <h2 className="mb-2 text-3xl font-bold">MediCoin Benefits</h2>
                <p className="text-lg opacity-90">Unlock Exclusive Rewards & Massive Savings</p>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Value Proposition */}
              <div className="p-6 text-center bg-gradient-to-r from-green-50 to-blue-50 rounded-xl">
                <h3 className="mb-2 text-2xl font-bold text-gray-900">🚀 Transform Your Health Journey into Wealth!</h3>
                <p className="text-lg text-gray-700">
                  MediCoin isn't just a cryptocurrency - it's your gateway to premium healthcare savings and exclusive benefits.
                </p>
              </div>

              {/* Benefits Grid */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Discount Tiers */}
                <div className="p-6 border border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center w-12 h-12 mr-4 bg-purple-500 rounded-full">
                      <CreditCard className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-purple-900">Enhanced Discounts</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-medium">Standard + MediCoin</span>
                      <span className="font-bold text-purple-600">10% OFF</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-medium">Silver + MediCoin</span>
                      <span className="font-bold text-purple-600">15% OFF</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-medium">Gold + MediCoin</span>
                      <span className="font-bold text-purple-600">20% OFF</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
                      <span className="font-medium">Platinum + MediCoin</span>
                      <span className="font-bold text-purple-700">25% OFF</span>
                    </div>
                  </div>
                </div>

                {/* Exclusive Benefits */}
                <div className="p-6 border border-blue-200 bg-gradient-to-br from-blue-50 to-green-50 rounded-xl">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center w-12 h-12 mr-4 bg-blue-500 rounded-full">
                      <Star className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-blue-900">Exclusive Perks</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center p-3 bg-white rounded-lg">
                      <CheckCircle className="w-5 h-5 mr-3 text-green-500" />
                      <span>Priority customer support</span>
                    </div>
                    <div className="flex items-center p-3 bg-white rounded-lg">
                      <CheckCircle className="w-5 h-5 mr-3 text-green-500" />
                      <span>Early access to new products</span>
                    </div>
                    <div className="flex items-center p-3 bg-white rounded-lg">
                      <CheckCircle className="w-5 h-5 mr-3 text-green-500" />
                      <span>VIP health consultations</span>
                    </div>
                    <div className="flex items-center p-3 bg-white rounded-lg">
                      <CheckCircle className="w-5 h-5 mr-3 text-green-500" />
                      <span>Free premium device upgrades</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Investment Value */}
              <div className="p-6 border border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-12 h-12 mr-4 bg-yellow-500 rounded-full">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-yellow-900">Investment Potential</h4>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="p-4 text-center bg-white rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">💰</p>
                    <p className="font-semibold">Appreciating Asset</p>
                    <p className="text-sm text-gray-600">Value grows with platform adoption</p>
                  </div>
                  <div className="p-4 text-center bg-white rounded-lg">
                    <p className="text-2xl font-bold text-green-600">📈</p>
                    <p className="font-semibold">Staking Rewards</p>
                    <p className="text-sm text-gray-600">Earn 6%-132% APY from 1-5 year staking</p>
                  </div>
                  <div className="p-4 text-center bg-white rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">🔒</p>
                    <p className="font-semibold">Limited Supply</p>
                    <p className="text-sm text-gray-600">Deflationary token economics</p>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="p-6 text-center text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
                <h3 className="mb-3 text-2xl font-bold">Ready to Maximize Your Savings?</h3>
                <p className="mb-4 opacity-90">
                  Start earning and holding MediCoin today to unlock these incredible benefits!
                </p>
                <div className="flex flex-col justify-center gap-3 sm:flex-row">
                  <button 
                    onClick={() => window.open('https://ico.medicureon.com/', '_blank')}
                    className="px-6 py-3 font-semibold text-purple-600 transition-all bg-white rounded-lg hover:bg-gray-100"
                  >
                    Buy MediCoin
                  </button>
                  <button 
                    onClick={() => window.open('https://ico.medicureon.com/videos', '_blank')}
                    className="px-6 py-3 font-semibold transition-all rounded-lg bg-white/20 backdrop-blur-sm hover:bg-white/30"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className="fixed z-50 p-4 text-white bg-green-500 rounded-lg shadow-lg bottom-4 right-4 animate-slide-up">
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default RewardsAchievementsDashboard;
